import React, {useState, useMemo, useEffect, useCallback, useRef} from "react";
import {toast} from "react-toastify";
import {NavLink} from "react-router-dom";
import {useQuery} from "@apollo/client";

import NavBar from "../../components/common/NavBar";
import PaginatedCardList from "../../components/pagination/PaginatedCardList";

import AddOrgModal from "../../components/organization/AddOrgModal";
import EditOrgModal from "../../components/organization/EditOrgModal";

import RemoveTeamMutation from "../../components/season/mutations/RemoveTeam";
import {GET_ORGANIZATIONS} from "../../components/organization/queries/GetOrganizations";

import InitialsAvatar from "../../util/name_initial";
import {confirmDelete} from "../../components/common/confirmDelete";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import {useBranding} from "../../context/BrandingContext";
import {alphaFromHex} from "../../util/colors";
import EditOrgBrandingModal from "../../components/organization/EditOrgBrandingModal";
import InviteCoordinatorModal from "../../components/organization/InviteCoordinatorModal";
import useTranslation from "../../i18n/useTranslation";
import useCheckOrganizationLimitOrPrompt from "../../components/common/hooks/useCheckOrganizationLimitOrPrompt";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

/* -----------------------------
   Page
------------------------------ */

export default function OrganizationsPage() {
    const colors = useThemeColors();
    const {isOrgPortal, ready} = useBranding();
    const {t} = useTranslation();

    // Layout / theme colors
    const primary = colors.primary;

    // ✅ Card palette: always light background + dark text
    const cardSurface = colors.white; // fallback only
    const cardText = colors.woodsmoke; // fallback only

    const borderBase = colors.border;

    const cardStyles = useMemo(() => {
        const borderSoft = alphaFromHex(borderBase, 0.14);
        const borderSofter = alphaFromHex(borderBase, 0.08);
        const mutedText = alphaFromHex(cardText, 0.6);
        const veryMutedText = alphaFromHex(cardText, 0.4);
        const softBg = alphaFromHex(cardText, 0.03);

        return {
            card: {
                borderRadius: 16,
                overflow: "hidden",
                border: `1px solid ${borderSofter}`,
                boxShadow: "0 14px 34px rgba(0,0,0,0.10)",
                transition:
                    "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
                background: cardSurface,
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                background: `linear-gradient(
                135deg,
                ${alphaFromHex(primary, 0.50)},
                ${alphaFromHex(cardSurface, 0.0)}
            )`,
                borderBottom: `1px solid ${borderSofter}`,
            },
            headerLeft: {display: "flex", alignItems: "center", gap: 8},

            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.02em",
                background: colors.secondary,
                color: colors.textOnSecondary,
                border: `1px solid ${alphaFromHex(colors.woodsmoke, 0.16)}`,
                textTransform: "uppercase",
            },

            // Plan pill (Free / Trial / Paid)
            planChipFree: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: alphaFromHex(borderBase, 0.10),
                color: cardText,
                border: `1px solid ${alphaFromHex(borderBase, 0.35)}`,
            },
            planChipTrial: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: alphaFromHex(colors.warning, 0.10),
                color: colors.warning,
                border: `1px solid ${alphaFromHex(colors.warning, 0.35)}`,
            },
            planChipPaid: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: alphaFromHex(colors.danger, 0.10),
                color: colors.woodsmoke,
                border: `1px solid ${alphaFromHex(primary, 0.35)}`,
            },

            body: {padding: 12, background: cardSurface},

            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: isOrgPortal ? colors.primary : colors.woodsmoke,
                lineHeight: 1.15,
            },

            sub: {
                marginTop: 6,
                fontSize: 12.5,
                fontWeight: 800,
                color: mutedText,
            },

            metaGrid: {
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 8,
            },
            metaRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 10px",
                borderRadius: 12,
                background: softBg,
                border: `1px solid ${borderSofter}`,
            },
            metaLabel: {
                fontSize: 12,
                fontWeight: 900,
                color: veryMutedText,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            metaValue: {
                fontSize: 12.5,
                fontWeight: 900,
                color: cardText,
            },
            statsRow: {
                marginTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
            },
            statPill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 800,
                background: softBg,
                border: `1px solid ${borderSofter}`,
                color: mutedText,
                whiteSpace: "nowrap",
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                background: alphaFromHex(cardSurface, 0.02),
                borderTop: `1px solid ${borderSofter}`,
            },
            actions: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: "auto",
            },

            btn: {
                borderRadius: 12,
                padding: "8px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: `1px solid ${borderSoft}`,
                fontSize: 12.5,
            },
            btnPrimary: {
                background: colors.primary,
                color: colors.textOnPrimary,
            },
            btnGhost: {
                background: cardSurface,
                color: cardText,
            },
            btnUpgrade: {
                background: colors.secondary,
                color: colors.textOnSecondary,
                border: `1px solid ${borderSoft}`,
            },
            btnDanger: {
                background: alphaFromHex(colors.danger, 0.08),
                border: `1px solid ${alphaFromHex(colors.danger, 0.25)}`,
                color: colors.danger,
            },
            iconBtn: {
                width: 36,
                height: 36,
                borderRadius: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: cardSurface,
                border: `1px solid ${borderSoft}`,
            },
            orgLogoWrap: {
                width: 64,
                height: 64,
                borderRadius: 16,
                overflow: "hidden",
                border: `1px solid ${borderSofter}`,
                background: softBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 6,
            },
            orgLogoImg: {
                width: "100%",
                height: "100%",
                objectFit: "cover",
            },
            orgTopRow: {
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
            },
        };
    }, [
        primary,
        cardSurface,
        cardText,
        borderBase,
        colors.primary,
        isOrgPortal,
        colors.danger,
        colors.secondary,
        colors.textOnPrimary,
        colors.textOnSecondary,
        colors.warning,
        colors.woodsmoke,
    ]);

    const [showModal, setShowModal] = useState(false);
    const [removeOrgMutation] = RemoveTeamMutation();

    const {activeProfile} = useActiveProfile();
    const activeProfileId = activeProfile?.id || null;
    const featureKeys = useMemo(() => new Set(
        (activeProfile?.profileOrganizations || []).flatMap((profileOrganization) =>
            profileOrganization?.organization?.activeSubscription?.plan?.features?.map((feature) => feature?.key) || []
        )
    ), [activeProfile?.profileOrganizations]);
    const managementResourceVariables = useMemo(() => ({
        includeWaivers: featureKeys.has("waiver_management"),
        includeVenues: featureKeys.has("venues_management"),
        includeSanctions: featureKeys.has("sanction_management"),
    }), [featureKeys]);

    const [page, setPage] = useState(1);

    const [editModalShow, setEditModalShow] = useState(false);
    const [editingOrganization, setEditingOrganization] = useState(null);

    const [brandingOrg, setBrandingOrg] = useState(null);
    const [showBrandingModal, setShowBrandingModal] = useState(false);
    const [coordinatorOrg, setCoordinatorOrg] = useState(null);
    const [showCoordinatorModal, setShowCoordinatorModal] = useState(false);

    const openBranding = (org) => {
        setBrandingOrg(org);
        setShowBrandingModal(true);
    };

    const openCoordinatorModal = (org) => {
        if (!org?.id) return;
        setCoordinatorOrg(org);
        setShowCoordinatorModal(true);
    };

    const closeCoordinatorModal = () => {
        setShowCoordinatorModal(false);
        setCoordinatorOrg(null);
    };

    const [fields, setFields] = useState([]);
    const [waivers, setWaivers] = useState([]);
    const [sanctionRules, setSanctionRules] = useState([]);
    const [stripeAccounts, setStripeAccounts] = useState([]);
    const previousProfileIdRef = useRef(activeProfileId);

    const queryPage = previousProfileIdRef.current === activeProfileId ? page : 1;

    useEffect(() => {
        if (!activeProfileId) return;
        previousProfileIdRef.current = activeProfileId;
        setPage(1);
    }, [activeProfileId]);

    const {
        data,
        loading,
        error,
        refetch,
        networkStatus,
    } = useQuery(GET_ORGANIZATIONS, {
        variables: {
            profileId: activeProfileId,
            page: queryPage,
            ...managementResourceVariables,
        },
        skip: !activeProfileId,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        onCompleted: (d) => {
            setFields(d?.venues?.objects || []);
            setWaivers(d?.waivers?.objects || []);
            setSanctionRules(d?.sanctionRules?.objects || []);
            setStripeAccounts(d?.stripeAccounts || []);
        },
    });

    const organizations = useMemo(() => {
        return data?.organizations?.objects ?? [];
    }, [data?.organizations?.objects]);

    const totalPages = useMemo(() => {
        return data?.organizations?.pages ?? 1;
    }, [data?.organizations?.pages]);

    const navBarElem = useMemo(
        () => ({
            pageTitle: t("organizations.pageTitle"),
            links: [
                {
                    pageTitle: t("organizations.pageTitle"),
                    navLink: null,
                    active: true,
                },
            ],
        }),
        [t]
    );

    const editOrganization = useCallback(
        (orgId) => {
            const org = organizations.find((o) => o.id === orgId);
            setEditingOrganization(org || null);
            setEditModalShow(true);
        },
        [organizations]
    );

    const removeOrg = useCallback(
        async (orgId) => {
            try {
                const res = await removeOrgMutation({
                    variables: {teamId: parseInt(orgId, 10)},
                });

                if (res?.data?.removeTeam?.success) {
                    toast.success(t("organizations.removeSuccess"), {
                        autoClose: 3000,
                        toastId: "remove_org_success",
                        position: "top-center",
                        closeOnClick: true,
                    });

                    await refetch({profileId: activeProfileId, page});
                } else {
                    toast.error(t("organizations.removeFailed"), {
                        autoClose: 3000,
                        toastId: "remove_org_failed",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            } catch (err) {
                toast.error(getLocalizedGraphQLError(err, t, "organizations.removeError"), {
                    autoClose: 3000,
                    toastId: "remove_org_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        },
        [removeOrgMutation, refetch, activeProfileId, page, t]
    );

    const requestDeleteOrg = useCallback(
        (org) => {
            confirmDelete({
                title: t("organizations.confirmArchiveTitle"),
                message: t("organizations.confirmArchiveMessage"),
                confirmLabel: t("organizations.confirmArchive"),
                cancelLabel: t("organizations.cancel"),
                danger: true,
                onConfirm: async () => {
                    await removeOrg(org.id);
                },
            });
        },
        [removeOrg, t]
    );

    const checkOrganizationLimitOrPrompt = useCheckOrganizationLimitOrPrompt();

    const handleAddOrganization = () => {
        const allowed = checkOrganizationLimitOrPrompt({
            currentCount: data?.organizations?.ownedOrganizationCount,
            currentLimit: data?.organizations?.ownedOrganizationLimit,
            itemLabel: "organizations",
            toast,
        });

        if (!allowed) return;

        setShowModal(true);
    };

    const isBusy = loading || networkStatus === 4; // 4 = refetch

    if (!ready) return null;

    return (
        <>
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem}/>

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "organizations.loadError")}
                        </div>
                    )}

                    <PaginatedCardList
                        key={activeProfileId || "no-profile"}
                        title={t("organizations.pageTitle")}
                        onAdd={handleAddOrganization}
                        page={page}
                        pages={totalPages}
                        onPageChange={setPage}
                        items={organizations}
                        loading={isBusy}
                        renderItem={(organization) => {
                            const orgType =
                                organization?.organizationType?.code?.toUpperCase?.() ||
                                "ORG";

                            const activeSub = organization.activeSubscription;

                            const planFeatures = Array.isArray(activeSub?.plan?.features)
                                ? activeSub.plan.features
                                : [];

                            const hasCustomBrandingFeature = planFeatures.some((f) => {
                                const key = (f?.key || "").toString().toLowerCase();
                                return key === "elite_custom_branding";
                            });

                            const hasManagerFeature = planFeatures.some((f) => {
                                const key = (f?.key || "").toString().toLowerCase();
                                return key === "manage_team_players";
                            });

                            const planKeyRaw =
                                activeSub?.plan?.key ||
                                activeSub?.plan?.code ||
                                activeSub?.plan?.name ||
                                "";
                            const planKey = planKeyRaw.toString().toLowerCase();

                            const isTrial =
                                activeSub?.isTrial === true ||
                                planKey === "trial" ||
                                planKey === "trial_plan";

                            const isFree =
                                !activeSub ||
                                planKey === "free" ||
                                planKey === "free_plan" ||
                                planKey === "starter";

                            const isEnterprise = planKey === "enterprise";

                            const shouldShowUpgradeCta = !isEnterprise;

                            const currentPlanLabel =
                                activeSub?.plan?.displayName ||
                                activeSub?.plan?.name ||
                                (isFree
                                    ? t("organizations.planFree")
                                    : isTrial
                                        ? t("organizations.planTrial")
                                        : t("organizations.planPaid"));

                            const planChipStyle = isFree
                                ? cardStyles.planChipFree
                                : isTrial
                                    ? cardStyles.planChipTrial
                                    : cardStyles.planChipPaid;

                            const city =
                                organization.city ||
                                organization.location?.city ||
                                "";
                            const state =
                                organization.state ||
                                organization.location?.state ||
                                "";

                            const locationText = city
                                ? state
                                    ? `${city}, ${state}`
                                    : city
                                : t("organizations.locationFallback");

                            return (
                                <div
                                    className="col-12 col-sm-6 col-md-6 d-flex align-items-stretch flex-column"
                                    key={organization.id}
                                >
                                    <div
                                        className="card d-flex flex-fill"
                                        style={cardStyles.card}
                                    >
                                        {/* Header */}
                                        <div style={cardStyles.header}>
                                            <div style={cardStyles.headerLeft}>
                                                <span
                                                    style={cardStyles.chip}
                                                    title={t("organizations.pageTitle")}
                                                >
                                                    <i
                                                        className="fas fa-building"
                                                        style={{
                                                            color: colors.textOnSecondary,
                                                        }}
                                                    />
                                                    {orgType}
                                                </span>
                                            </div>

                                            <span
                                                style={planChipStyle}
                                                title={t("organizations.pageTitle")}
                                            >
                                                <i
                                                    className={
                                                        isFree
                                                            ? "fas fa-seedling"
                                                            : isTrial
                                                                ? "fas fa-hourglass-half"
                                                                : "fas fa-crown"
                                                    }
                                                    style={{
                                                        color: cardText,
                                                    }}
                                                />
                                                {currentPlanLabel.toUpperCase()}
                                            </span>
                                        </div>

                                        {/* Body */}
                                        <div style={cardStyles.body}>
                                            <div style={cardStyles.orgTopRow}>
                                                <div
                                                    style={{
                                                        flex: "0 0 auto",
                                                    }}
                                                >
                                                    {organization.logoUrl ? (
                                                        <div style={cardStyles.orgLogoWrap}>
                                                            <img
                                                                src={organization.logoUrl}
                                                                alt={organization.name || "Organization Logo"}
                                                                style={cardStyles.orgLogoImg}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <InitialsAvatar
                                                            name={organization.name}
                                                            size={64}
                                                            backgroundColor={colors.secondary}
                                                            textColor={colors.textOnSecondary}
                                                        />
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    <h3
                                                        style={cardStyles.title}
                                                        className="text-truncate"
                                                    >
                                                        {organization.name}
                                                    </h3>

                                                    <div style={cardStyles.sub}>
                                                        {locationText}
                                                    </div>

                                                    <div style={cardStyles.statsRow}>
                                                        <span style={cardStyles.statPill}>
                                                            <i className="fas fa-sitemap"/>
                                                            {organization.divisionsCount ?? 0}{" "}
                                                            {t("organizations.divisions")}
                                                        </span>

                                                        {typeof organization.seasonsCount ===
                                                            "number" && (
                                                                <span style={cardStyles.statPill}>
                                                                <i className="fas fa-calendar-alt"/>
                                                                    {organization.seasonsCount}{" "}
                                                                    {t("organizations.seasons")}
                                                            </span>
                                                            )}

                                                        {typeof organization.teamsCount ===
                                                            "number" && (
                                                                <span style={cardStyles.statPill}>
                                                                <i className="fas fa-users"/>
                                                                    {organization.teamsCount}{" "}
                                                                    {t("organizations.teams")}
                                                            </span>
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer */}
                                        <div style={cardStyles.footer}>
                                            <NavLink
                                                to={`${organization.slug}/divisions`}
                                                state={{waivers}}
                                                className="btn"
                                                style={{
                                                    ...cardStyles.btn,
                                                    ...cardStyles.btnPrimary,
                                                }}
                                                title={t("organizations.openDivisions")}
                                            >
                                                <i className="fas fa-th"/>
                                                {t("organizations.divisions")}
                                            </NavLink>

                                            {shouldShowUpgradeCta && (
                                                <NavLink
                                                    to="/subscription/plans"
                                                    state={{
                                                        defaultTab: "organization",
                                                        organizationSlug: organization.slug,
                                                    }}
                                                    className="btn"
                                                    style={{
                                                        ...cardStyles.btn,
                                                        ...cardStyles.btnUpgrade,
                                                    }}
                                                    title={t("organizations.upgrade")}
                                                >
                                                    <i className="fas fa-arrow-up"/>
                                                    {t("organizations.upgrade")}
                                                </NavLink>
                                            )}

                                            <div style={cardStyles.actions}>
                                                {hasManagerFeature && (
                                                    <button
                                                        type="button"
                                                        className="btn"
                                                        style={cardStyles.iconBtn}
                                                        onClick={() => openCoordinatorModal(organization)}
                                                        title={t("organizations.coordinators")}
                                                    >
                                                        <i className="fas fa-users"/>
                                                    </button>
                                                )}

                                                {hasCustomBrandingFeature && (
                                                    <button
                                                        type="button"
                                                        className="btn"
                                                        style={cardStyles.iconBtn}
                                                        onClick={() => openBranding(organization)}
                                                        title={t("organizations.brandingTheme")}
                                                    >
                                                        <i className="fas fa-palette"/>
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    className="btn"
                                                    style={cardStyles.iconBtn}
                                                    onClick={() =>
                                                        editOrganization(organization.id)
                                                    }
                                                    title={t("organizations.editOrganization")}
                                                >
                                                    <i className="fas fa-edit"/>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn"
                                                    style={{
                                                        ...cardStyles.iconBtn,
                                                        ...cardStyles.btnDanger,
                                                    }}
                                                    onClick={() =>
                                                        requestDeleteOrg(organization)
                                                    }
                                                    title={t("organizations.archiveOrganization")}
                                                >
                                                    <i className="fas fa-trash"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </section>
            </div>

            <AddOrgModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={() => {
                    setShowModal(false);
                    refetch({profileId: activeProfileId, page: 1});
                }}
                organizations={organizations}
                setOrganizations={() => {
                }}
                fields={fields}
                waivers={waivers}
                sanctionRules={sanctionRules}
                stripeAccounts={stripeAccounts}
            />

            <EditOrgModal
                show={editModalShow}
                onClose={() => setEditModalShow(false)}
                organization={editingOrganization}
                setOrganizations={() => {
                }}
                fields={fields}
                waivers={waivers}
                sanctionRules={sanctionRules}
                stripeAccounts={stripeAccounts}
            />

            <EditOrgBrandingModal
                show={showBrandingModal}
                organization={brandingOrg}
                onClose={() => setShowBrandingModal(false)}
                onUpdated={() => {
                    refetch();
                }}
            />

            <InviteCoordinatorModal
                show={showCoordinatorModal}
                onClose={closeCoordinatorModal}
                organizationId={coordinatorOrg?.id}
                organizationName={coordinatorOrg?.name}
                profileId={activeProfileId}
                colors={colors}
            />
        </>
    );
}
